import React, { useState, useRef, useEffect } from 'react';
import { FaShieldAlt, FaKey, FaArrowLeft } from 'react-icons/fa';

interface TwoFactorVerificationProps {
  onVerify: (code: string) => Promise<void>;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

export const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  onVerify,
  onBack,
  loading,
  error
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0] && !isBackupCode) {
      inputRefs.current[0].focus();
    }
  }, [isBackupCode]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'Enter') {
      handleSubmit(code.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '');
    
    if (pastedText.length === 6) {
      const newCode = pastedText.split('');
      setCode(newCode);
      // Auto-submit pasted code
      handleSubmit(pastedText);
    }
  };

  const handleSubmit = async (codeToSubmit?: string) => {
    const finalCode = codeToSubmit || (isBackupCode ? backupCode : code.join(''));
    
    if (!finalCode || (isBackupCode ? finalCode.length < 6 : finalCode.length !== 6)) {
      return;
    }

    await onVerify(finalCode);
  };

  const clearCode = () => {
    setCode(['', '', '', '', '', '']);
    setBackupCode('');
    if (!isBackupCode) {
      inputRefs.current[0]?.focus();
    }
  };

  const toggleBackupMode = () => {
    setIsBackupCode(!isBackupCode);
    clearCode();
  };

  const isCodeComplete = isBackupCode 
    ? backupCode.length >= 6 
    : code.every(digit => digit);

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <FaShieldAlt className="text-blue-600 text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Two-Factor Authentication
        </h2>
        <p className="text-gray-600">
          {isBackupCode 
            ? 'Enter one of your backup codes'
            : 'Enter the 6-digit code from your authenticator app'
          }
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Code Input */}
      <div className="mb-6">
        {!isBackupCode ? (
          <div className="flex justify-center space-x-3 mb-4">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center text-xl font-mono border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                maxLength={1}
                disabled={loading}
              />
            ))}
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Backup Code
            </label>
            <input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Enter backup code"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-center"
              disabled={loading}
              autoFocus
            />
          </div>
        )}

        {/* Toggle backup code mode */}
        <div className="text-center mb-6">
          <button
            onClick={toggleBackupMode}
            className="text-sm text-blue-600 hover:text-blue-800 underline"
            disabled={loading}
          >
            <FaKey className="inline mr-1" />
            {isBackupCode 
              ? 'Use authenticator app instead'
              : 'Use backup code instead'
            }
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={() => handleSubmit()}
          disabled={loading || !isCodeComplete}
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify'
          )}
        </button>

        <button
          onClick={clearCode}
          disabled={loading}
          className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          Clear
        </button>

        <button
          onClick={onBack}
          disabled={loading}
          className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 flex items-center justify-center disabled:opacity-50"
        >
          <FaArrowLeft className="mr-2" />
          Back to Login
        </button>
      </div>

      {/* Help text */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          {isBackupCode 
            ? 'Backup codes are 8 characters long and can only be used once'
            : 'Can\'t access your authenticator? Use a backup code instead'
          }
        </p>
      </div>
    </div>
  );
};